// api
import type {
  ReadAlarmGroupDto,
  ReadAlarmTypeDto,
} from '@/api/generated/recalc/endpoints/v1/alarms';

const ENERGY_XPLORER_ALARM_TYPES = [
  {
    alarmTypeName: 'Electricity system alarm',
    alarmType: 'ElectricitySystem',
  },
] as const;

type EnergyXplorerAlarmTypeName =
  (typeof ENERGY_XPLORER_ALARM_TYPES)[number]['alarmTypeName'];
type EnergyXplorerAlarmType =
  (typeof ENERGY_XPLORER_ALARM_TYPES)[number]['alarmType'];

export function isEnergyXplorerAlarmType(
  alarmType: string | null | undefined,
): alarmType is EnergyXplorerAlarmType {
  return ENERGY_XPLORER_ALARM_TYPES.some(
    (energyXplorerAlarmType) => energyXplorerAlarmType.alarmType === alarmType,
  );
}

export function isEnergyXplorerAlarmTypeName(
  alarmTypeName: string | null | undefined,
): alarmTypeName is EnergyXplorerAlarmTypeName {
  return ENERGY_XPLORER_ALARM_TYPES.some(
    (energyXplorerAlarmType) =>
      energyXplorerAlarmType.alarmTypeName === alarmTypeName,
  );
}

export function getEnergyXplorerAlarmTypeIds(
  alarmTypes: ReadAlarmTypeDto[],
): number[] {
  return alarmTypes
    .filter(
      (alarmType) =>
        typeof alarmType.AlarmTypeId === 'number' &&
        isEnergyXplorerAlarmTypeName(alarmType.Name),
    )
    .map((alarmType) => alarmType.AlarmTypeId);
}

export function getCoveredEnergyXplorerAlarmTypeIds(
  alarmGroups: ReadAlarmGroupDto[],
  userId: number,
): number[] {
  const coveredIds = new Set<number>();

  alarmGroups.forEach((group) => {
    const isSubscribed = group.AlarmSubscriptions?.some(
      (subscription) => subscription.User?.UserId === userId,
    );

    if (!isSubscribed) {
      return;
    }

    group.AlarmInclusions?.forEach((inclusion) => {
      const alarmTypeId = inclusion.AlarmType?.AlarmTypeId;
      const alarmTypeName = inclusion.AlarmType?.Name;

      if (
        typeof alarmTypeId === 'number' &&
        isEnergyXplorerAlarmTypeName(alarmTypeName)
      ) {
        coveredIds.add(alarmTypeId);
      }
    });
  });

  return [...coveredIds];
}

export function findAutoCreatedEnergyXplorerAlarmGroup(
  alarmGroups: ReadAlarmGroupDto[],
  userId: number,
  groupName: string,
): ReadAlarmGroupDto | null {
  if (!groupName) {
    return null;
  }

  return (
    alarmGroups.find((group) => {
      const isSubscribed = group.AlarmSubscriptions?.some(
        (subscription) => subscription.User?.UserId === userId,
      );

      const includesEnergyXplorerAlarm = group.AlarmInclusions?.some(
        (inclusion) =>
          isEnergyXplorerAlarmTypeName(inclusion.AlarmType?.Name) ?? false,
      );

      return (
        group.Name === groupName &&
        isSubscribed &&
        includesEnergyXplorerAlarm === true
      );
    }) ?? null
  );
}

export function getMissingEnergyXplorerAlarmTypeIds(
  alarmGroups: ReadAlarmGroupDto[],
  userId: number,
  requiredAlarmTypeIds: number[],
): number[] {
  const coveredIds = new Set(
    getCoveredEnergyXplorerAlarmTypeIds(alarmGroups, userId),
  );

  return requiredAlarmTypeIds.filter(
    (alarmTypeId) => !coveredIds.has(alarmTypeId),
  );
}
